import Button from '@srm/ui/button';
import Hint from '@srm/ui/hint';
import { DocumentAttachmentChangesContext } from '@/context/DocumentAttachmentChangesContext';
import { highlightDocumentField } from '../../../documentFieldNavigation';
import './AttachGrid.scss';
import { FC, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { TableTab } from '@models/document/IDocumentScheme';
import Preloader from '@srm/ui/preloader';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import { FilesService } from '@services/FilesService';
import { useFileDownload } from '@hooks/useFileDownload';
import { useAttachGridData } from './useAttachGridData';
import { useAttachSelection } from './useAttachSelection';
import { AttachView, IAttachRow } from './AttachGrid.types';
import AttachToolbar from './parts/AttachToolbar/AttachToolbar';
import AttachBulkBar from './parts/AttachBulkBar/AttachBulkBar';
import AttachPager from './parts/AttachPager/AttachPager';
import AttachEmpty from './parts/AttachEmpty/AttachEmpty';
import AttachTable from './parts/AttachTable/AttachTable';
import AttachGallery from './parts/AttachGallery/AttachGallery';
import { HiOutlineArrowDownTray, HiOutlineFolderOpen, HiOutlinePaperClip } from 'react-icons/hi2';
import AttachPreviewModal from './AttachPreviewModal';
import AttachMoreActions from './parts/AttachMoreActions/AttachMoreActions';
import { primaryColumn } from './attachRowView';
import { IAttachSavedView, fromGridStateSettings, toGridStateSettings } from './attachViews';
import { useAttachViews } from './useAttachViews';
import StateModal from '@organisms/StateModal/StateModal';
import { IGridStateSettings } from '@models/response/IGridStateSettings';
import { exportAttachesToExcel } from './attachExcelExport';
import { AttachFilesExecutorModal } from '@organisms/actions/docExecutors/AttachFiles/AttachFilesExecutorModal';
import { DataTableWrapMode, IDataTableHandle, isDataTableWrapMode } from '@srm/ui/data-table';
import { IBaseAction } from '@models/actions/IBaseAction';
import { DocumentRuntimeContext, isDocumentRuntimeReadOnly } from '@/context/DocumentRuntimeContext';
import TableSchemeDebugButton from '@molecules/DebugPanel/TableSchemeDebug/TableSchemeDebugButton';
import { toSchemeDebugColumns } from '@molecules/DebugPanel/TableSchemeDebug/tableSchemeDebug';

interface AttachGridProps {
    docId: string;
    tableTab: TableTab;
    testId?: string;
}

// Пер-таблица: «последний выбранный» режим и перенос хранятся по ключу таблицы, чтобы
// переключение карточек на вложениях одного документа не становилось дефолтом для всех.
const viewStorageKey = (tableKey: string) => `attachGrid.view.${tableKey}`;
const wrapStorageKey = (tableKey: string) => `attachGrid.wrap.${tableKey}`;

const isAttachView = (v: string | null): v is AttachView => v === 'table' || v === 'cards' || v === 'compact';

const readView = (tableKey: string): AttachView => {
    try {
        const v = localStorage.getItem(viewStorageKey(tableKey));
        if (isAttachView(v)) return v;
    } catch {
        /* ignore */
    }
    return 'table';
};

const readWrapMode = (tableKey: string): DataTableWrapMode => {
    try {
        const v = localStorage.getItem(wrapStorageKey(tableKey));
        if (isDataTableWrapMode(v)) return v;
        // прежний формат хранил булев перенос ('1'/'0')
        if (v === '0') return 'nowrap';
    } catch {
        /* ignore */
    }
    return 'wrap';
};

/** Грид вложений документа в стиле «Документация & руководства»: таблица
 *  (строки-карточки) + карточные виды, фильтры по типам, мультисортировка, версии. */
const AttachGrid: FC<AttachGridProps> = ({ docId, tableTab, testId }) => {
    const runtime = useContext(DocumentRuntimeContext);
    const readOnly = isDocumentRuntimeReadOnly(runtime);
    const attachmentChanges = useContext(DocumentAttachmentChangesContext);
    const gridRef = useRef<HTMLDivElement>(null);
    const leadRef = useRef<HTMLDivElement>(null);
    const locatingRef = useRef<number | undefined>(undefined);
    const data = useAttachGridData(docId, tableTab.key);
    const { ready: dataReady, setSpotlight } = data;
    const locate = attachmentChanges?.documentId === docId && attachmentChanges.request?.tableKey === tableTab.key
        ? attachmentChanges.request : undefined;
    useEffect(() => {
        if (!dataReady || !locate || locatingRef.current === locate.id) return;
        locatingRef.current = locate.id;
        setSpotlight(locate.items.map((item) => item.attachmentKey));
    }, [locate, dataReady, setSpotlight]);
    useEffect(() => {
        if (!locate || data.loading || !data.ready || !data.spotlightKeys || data.loadedSpotlightKeys !== data.spotlightKeys) return;
        const root = gridRef.current;
        if (!root || root.closest('[aria-hidden="true"]')) return;
        const timer = setTimeout(() => {
            const rows = Array.from(root.querySelectorAll<HTMLElement>('[data-row-key]'));
            const target = rows.find((element) => data.spotlightKeys?.includes(element.dataset.rowKey ?? ''));
            if (target) {
                target.scrollIntoView({ behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth', block: 'center' });
                highlightDocumentField(target);
                target.setAttribute('tabindex', '-1');
                target.focus({ preventScroll: true });
            }
            attachmentChanges?.handled(locate.id);
        }, 100);
        return () => clearTimeout(timer);
    }, [locate, data.loading, data.ready, data.spotlightKeys, data.loadedSpotlightKeys, data.rows, attachmentChanges]);
    useEffect(() => {
        if (data.loading || data.error || !data.loadedChangeToken) return;
        const mark = () => {
            if (document.visibilityState !== 'hidden' && gridRef.current && !gridRef.current.closest('[aria-hidden="true"]'))
                attachmentChanges?.markViewed(tableTab.key, data.loadedChangeToken!, data.rows.map((row) => row.key));
        };
        mark();
        const panel = gridRef.current?.closest('[role="tabpanel"]');
        const observer = new MutationObserver(mark);
        if (panel) observer.observe(panel, { attributes: true, attributeFilter: ['aria-hidden'] });
        document.addEventListener('visibilitychange', mark);
        return () => { observer.disconnect(); document.removeEventListener('visibilitychange', mark); };
    }, [data.loading, data.error, data.loadedChangeToken, data.rows, tableTab.key, attachmentChanges]);
    const selection = useAttachSelection(
        data.allRows,
        data.childrenByParent,
        data.options?.selectionRecursive ?? false,
    );
    const tableRef = useRef<IDataTableHandle<IAttachRow>>(null);
    const clearSelection = () => {
        tableRef.current?.clearSelection();
        selection.clear();
    };

    const actionService = useMemo(
        () => (readOnly ? undefined : new AttachActionsService(docId, '-1')),
        [docId, readOnly],
    );

    // Скачивание авторизованно (Bearer-заголовок в $api), без access_token в URL
    // (иначе долгоживущий токен утекает в history/логи/devtools).
    // Колбэки строк стабильны: от их ссылок зависят колонки и кэш строк таблицы, а грид
    // перерисовывается на каждую отметку.
    const { run: runRowDownload, isBusy: isRowDownloadBusy } = useFileDownload();
    const onDownload = useMemo(
        () =>
            readOnly
                ? undefined
                : (row: IAttachRow) =>
                      void runRowDownload(
                          () => FilesService.downloadFile(`attachment/download/${row.documentKey}/${row.key}`),
                          row.key,
                      ),
        [readOnly, runRowDownload],
    );
    const isDownloading = useCallback((row: IAttachRow) => isRowDownloadBusy(row.key), [isRowDownloadBusy]);

    const [view, setView] = useState<AttachView>(() => (readOnly ? 'table' : readView(tableTab.key)));
    const [filterVisible, setFilterVisible] = useState(false);
    const [previewRow, setPreviewRow] = useState<IAttachRow | null>(null);
    const [wrapMode, setWrapMode] = useState<DataTableWrapMode>(() => (readOnly ? 'wrap' : readWrapMode(tableTab.key)));
    const {
        rawViews,
        setRawViews,
        defaultView,
        loaded: viewsLoaded,
        service: stateService,
    } = useAttachViews(tableTab.key, !readOnly);
    const [viewsOpen, setViewsOpen] = useState(false);
    const [addOpen, setAddOpen] = useState(false);
    const addAttachActionKey = data.options?.addAttachActionKey;

    useEffect(() => {
        if (readOnly) return;
        try {
            localStorage.setItem(viewStorageKey(tableTab.key), view);
        } catch {
            /* ignore */
        }
    }, [readOnly, view, tableTab.key]);

    useEffect(() => {
        if (readOnly) return;
        try {
            localStorage.setItem(wrapStorageKey(tableTab.key), wrapMode);
        } catch {
            /* ignore */
        }
    }, [readOnly, wrapMode, tableTab.key]);

    const canSelectAll = data.options?.selectionMode !== 'single' && data.options?.allowSelectAll !== false;

    // Текущий выбор (весь набор + версии) для групповых операций — не только текущая страница.
    const pickedRows = [...data.allRows, ...Object.values(data.childrenByParent).flat()].filter((r) =>
        selection.selected.has(r.key),
    );
    const pickedKeys = pickedRows.map((r) => r.key);
    const pickedData = pickedRows.map((r) => r.raw);

    // Прямое «Скачать» по выбранным — один запрос со склеенными ключами,
    // как DownloadExecutor: attachment/download/{docId}/{key1,key2,...}.
    const selectedDownload = useFileDownload();
    const onDownloadSelected = () => {
        if (readOnly || !pickedKeys.length) return;
        void selectedDownload.run(() =>
            FilesService.downloadFile(`attachment/download/${docId}/${pickedKeys.join(',')}`),
        );
    };

    // ----- Сохранённые виды (сервер): режим + перенос + видимость колонок + сортировка -----
    const { setColumnsVisibility, setSort } = data;
    const applyView = useCallback(
        (v: IAttachSavedView) => {
            setView(v.view);
            setWrapMode(v.wrapMode);
            setColumnsVisibility(v.hidden);
            setSort(v.sort);
        },
        [setColumnsVisibility, setSort],
    );
    /** Снимок текущего состояния для StateModal (окно «Состояния таблицы»). */
    const captureState = (name: string, previous?: IGridStateSettings): IGridStateSettings => {
        const hidden = data.columns.filter((c) => c.showInColumnChooser && !c.show).map((c) => c.field);
        const snap = toGridStateSettings({
            name,
            view,
            wrapMode,
            hidden,
            sort: data.sort,
            default: previous?.default,
        });
        return { ...snap, selected: previous?.selected ?? false };
    };
    /** Выбор состояния в StateModal применяет его к гриду. */
    const onSelectState = (next: IGridStateSettings[] | undefined) => {
        markTouched();
        setRawViews(next);
        const selected = next?.find((s) => s.selected);
        if (selected) applyView(fromGridStateSettings(selected));
    };

    // Авто-применение вида «по умолчанию» — только при ПЕРВОЙ загрузке и только если
    // пользователь ещё ничего не трогал. defaultView приходит асинхронно (fetch видов),
    // поэтому решение принимаем ровно один раз — когда виды загружены (viewsLoaded),
    // сверяясь с userTouchedRef; иначе поздний default перетёр бы ручной выбор пользователя.
    const userTouchedRef = useRef(false);
    const autoAppliedRef = useRef(false);
    useEffect(() => {
        autoAppliedRef.current = false;
        userTouchedRef.current = false;
    }, [tableTab.key]);
    useEffect(() => {
        if (autoAppliedRef.current || !data.ready || !viewsLoaded) return;
        autoAppliedRef.current = true;
        if (defaultView && !userTouchedRef.current) applyView(defaultView);
    }, [data.ready, viewsLoaded, defaultView, applyView]);

    // Любое пользовательское изменение вида в окне до загрузки видов отменяет авто-default.
    const markTouched = () => {
        userTouchedRef.current = true;
    };
    const onUserViewChange = (v: AttachView) => {
        markTouched();
        setView(v);
    };
    const onUserSort = (field: string, dir: 'asc' | 'desc') => {
        markTouched();
        data.setSingleSort(field, dir);
    };
    const onUserWrapMode = (mode: DataTableWrapMode) => {
        markTouched();
        setWrapMode(mode);
    };
    // Экспорт текущего (отфильтрованного/отсортированного) набора в Excel — клиентский
    // (данные уже в памяти: allRows; серверного эндпоинта для аттачей нет).
    const onExport = () => {
        void exportAttachesToExcel(data.allRows, data.columns, `Вложения_${docId}.xlsx`);
    };

    const isTable = view === 'table';
    const sortableColumns = data.columns.filter((c) => c.allowSorting);
    const showEmpty = data.ready && !data.loading && !data.error && data.rows.length === 0;
    const hasActiveFilters = Object.keys(data.filters).length > 0;
    // Пустой результат поиска/фильтра ≠ таблица без вложений — разный текст.
    const isFiltered = hasActiveFilters || !!data.search;

    // Отжатие «Фильтры» (скрытие строки) сбрасывает сами фильтры. Но только если они
    // заданы — иначе пустой сброс менял бы идентичность filters и зря триггерил рефетч.
    const onToggleFilter = () => {
        markTouched();
        if (filterVisible && hasActiveFilters) data.clearFilters();
        setFilterVisible((v) => !v);
    };

    // «Выбор столбцов» — канонический чузер базовой таблицы; первая (закреплённая)
    // колонка не скрывается, видимостью владеет грид вложений (сохранённые виды).
    const lockedField = primaryColumn(data.columns)?.field;
    const chooserColumns = data.columns
        .filter((c) => c.showInColumnChooser)
        .map((c) => ({ id: c.field, header: c.header, visible: c.show, locked: c.field === lockedField }));
    // пакет видимости одним setState: новый список скрытых из текущего ± изменения
    const onColumnsApply = (updates: Record<string, boolean>) => {
        markTouched();
        const hidden = data.columns
            .filter((c) => c.showInColumnChooser && !(updates[c.field] ?? c.show))
            .map((c) => c.field);
        setColumnsVisibility(hidden);
    };

    const onPreview = useMemo(() => (readOnly ? undefined : (row: IAttachRow) => setPreviewRow(row)), [readOnly]);

    return (
        <div ref={gridRef} className="atb" data-testid={testId ? `attach-grid-${testId}` : undefined}>
            {!data.spotlightKeys && attachmentChanges?.unread?.some((item) => item.tableKeys.includes(tableTab.key)) &&
                <Hint className="atb__changed-files" tone="info" maxWidth="none">
                    <div className="atb__changed-files-content">
                        <span>Есть изменения файлов</span>
                        <Button variant="text" size="sm" onClick={() => attachmentChanges.revealUnread?.(tableTab.key)}>Показать изменения</Button>
                    </div>
                </Hint>}
            {data.spotlightKeys && <Hint className="atb__changed-files" tone="info" maxWidth="none">
                <div className="atb__changed-files-content">
                    <span>{!data.loading && !data.rows.length ? 'Файл больше недоступен' : 'Показаны изменённые файлы'}</span>
                    <Button variant="text" size="sm" onClick={() => data.setSpotlight(null)}>Вернуть фильтры</Button>
                </div>
            </Hint>}
            <section className="atb__panel">
                {/* туллбар и действия над выбранными прилипают над шапкой таблицы (stickyHeader) */}
                <div ref={leadRef} className={`atb__lead${isTable ? ' atb__lead--sticky' : ''}`}>
                    <AttachToolbar
                        search={data.search}
                        onSearch={data.setSearch}
                        onSearchInput={markTouched}
                        sortColumns={sortableColumns}
                        sort={data.sort}
                        onSortColumn={onUserSort}
                        view={view}
                        onViewChange={onUserViewChange}
                        filterVisible={filterVisible}
                        onToggleFilter={onToggleFilter}
                        showFilterToggle={isTable}
                        hasActiveFilters={hasActiveFilters}
                        onClearFilters={data.clearFilters}
                        onExport={onExport}
                        onAddFiles={!readOnly && addAttachActionKey ? () => setAddOpen(true) : undefined}
                        chooserColumns={readOnly ? undefined : chooserColumns}
                        onColumnsApply={readOnly ? undefined : onColumnsApply}
                        wrapMode={readOnly ? undefined : wrapMode}
                        onWrapModeChange={readOnly ? undefined : onUserWrapMode}
                        onOpenViews={readOnly ? undefined : () => setViewsOpen(true)}
                        schemeDebug={
                            <TableSchemeDebugButton
                                title={tableTab.name}
                                tableKey={tableTab.key}
                                visibilityRules={tableTab.visibilityRules}
                                sourcePath={`/document/attachScheme/${docId}/${tableTab.key}`}
                                buttonClassName="atb__icon-btn"
                                columns={toSchemeDebugColumns(data.schemeColumns)}
                                details={
                                    data.options?.stateKey
                                        ? [{ label: 'stateKey', value: data.options.stateKey }]
                                        : undefined
                                }
                            />
                        }
                    />

                    {!readOnly && actionService && (
                        <AttachBulkBar
                            count={selection.count}
                            onClear={clearSelection}
                            total={data.allRows.length}
                            onSelectAll={canSelectAll && !selection.allSelected ? selection.selectAll : undefined}
                        >
                            <button
                                type="button"
                                className="atb__chip"
                                aria-busy={selectedDownload.busy || undefined}
                                disabled={selectedDownload.busy}
                                onClick={onDownloadSelected}
                            >
                                {selectedDownload.busy ? (
                                    <Preloader size={16} variant="inherit" position="inline" />
                                ) : (
                                    <HiOutlineArrowDownTray aria-hidden />
                                )}
                                Скачать
                            </button>
                            <AttachMoreActions
                                service={actionService}
                                objIds={pickedKeys.join(',')}
                                objData={pickedData}
                                onModifyData={data.refresh}
                            />
                        </AttachBulkBar>
                    )}
                </div>

                <div className="atb__content">
                    {isTable ? (
                        <AttachTable
                            controlRef={tableRef}
                            data={data}
                            selection={selection}
                            selectionActive={!readOnly && selection.count > 0}
                            filterVisible={filterVisible}
                            actionService={actionService}
                            onDownload={onDownload}
                            isDownloading={isDownloading}
                            onPreview={onPreview}
                            wrapMode={wrapMode}
                            readOnly={readOnly}
                            stickyLeadRef={leadRef}
                            emptyState={
                                showEmpty
                                    ? {
                                          title: isFiltered ? 'Ничего не найдено' : 'Вложений пока нет',
                                          hint: isFiltered
                                              ? 'Измените запрос или сбросьте фильтры по колонкам.'
                                              : 'К этому документу ещё не прикреплены файлы.',
                                          icon: isFiltered ? (
                                              <HiOutlineFolderOpen aria-hidden />
                                          ) : (
                                              <HiOutlinePaperClip aria-hidden />
                                          ),
                                      }
                                    : undefined
                            }
                        />
                    ) : (
                        <AttachGallery
                            data={data}
                            selection={selection}
                            selectionActive={!readOnly && selection.count > 0}
                            view={view}
                            actionService={actionService}
                            onDownload={onDownload}
                            isDownloading={isDownloading}
                            onPreview={onPreview}
                            readOnly={readOnly}
                        />
                    )}

                    {showEmpty &&
                        !isTable &&
                        (isFiltered ? (
                            <AttachEmpty />
                        ) : (
                            <AttachEmpty
                                Icon={HiOutlinePaperClip}
                                title="Вложений пока нет"
                                hint="К этому документу ещё не прикреплены файлы."
                            />
                        ))}

                    {data.error && (
                        <AttachEmpty
                            title="Не удалось загрузить вложения"
                            hint={readOnly ? 'Preview source не вернул данные таблицы.' : 'Повторите загрузку вкладки.'}
                        />
                    )}

                    {(data.loading || (!data.ready && !data.error)) && (
                        <div className="atb__loading">
                            <Preloader size="lg" />
                        </div>
                    )}
                </div>

                {data.options && (
                    <AttachPager
                        page={data.page}
                        pageSize={data.pageSize}
                        totalCount={data.totalCount}
                        pagerSizes={data.options.pagerSizes}
                        showPageSizeSelector={data.options.showPageSizeSelector}
                        showInfo={data.options.showPageInfo}
                        onPageChange={data.setPage}
                        onPageSizeChange={data.setPageSize}
                    />
                )}
            </section>

            {!readOnly && <AttachPreviewModal row={previewRow} rows={data.rows} onClose={() => setPreviewRow(null)} />}

            {!readOnly && (
                <StateModal
                    opened={viewsOpen}
                    stateKey={tableTab.key}
                    stateService={stateService}
                    stateSettings={rawViews}
                    captureState={captureState}
                    onSelectState={onSelectState}
                    onClose={() => setViewsOpen(false)}
                />
            )}

            {!readOnly && addOpen && addAttachActionKey && (
                <AttachFilesExecutorModal
                    docId={docId}
                    action={
                        {
                            key: addAttachActionKey,
                            type: '',
                            displayName: 'Добавить файлы',
                            options: {},
                            hasAccessibility: true,
                            hasGroupAccessibility: false,
                        } as IBaseAction
                    }
                    okButtonText="ОК"
                    cancelButtonText="Отмена"
                    modalSize="xl"
                    completeHandler={(ok) => {
                        setAddOpen(false);
                        if (ok) data.refresh();
                    }}
                />
            )}
        </div>
    );
};

export default AttachGrid;
